import { Skeleton, toast } from "@credit-acceptance/uikit";
import { CancelledError, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import PageFooter from "../../components/layout/PageFooter";
import TopBar from "../../components/layout/TopBar";
import TwoColumnLayout from "../../components/layout/TwoColumnLayout";
import { ErrorState } from "../../components/ui/centeredMessage";
import { useAuth } from "../../hooks/useAuth";
import { useFinancialAccessGuard } from "../../hooks/useFinancialAccessGuard";
import { computeShouldForceExit, useForceExitCheck } from "../../hooks/useForceExitCheck";
import { useWorkbenchPermissions } from "../../hooks/useWorkbenchPermissions";
import { redirectToOriginSite } from "../../lib/exitRedirect";
import { isContractCompleted } from "../../lib/permissions/dealState/isContractCompleted";
import { isPreApprovalApp } from "../../lib/permissions/dealState/isPreApprovalApp";
import { getErrorMessage } from "../../lib/query/errors";
import { runAddressVerificationGate } from "../../stores/addressVerificationGateStore";
import { useLockReadOnly } from "../../stores/approvalStore";
import { useForceExitStore } from "../../stores/forceExitStore";
import { getHasCombinedIncomeChanged } from "../../stores/incomeChangeStore";
import { useSaveStatusStore } from "../../stores/saveStatusStore";
import ActionFabCluster from "../actions/ActionFabCluster";
import { useDocumentActionListener } from "../documents/coord/useDocumentActionListener";
import { useDocumentBroadcaster } from "../documents/coord/useDocumentBroadcaster";
import DocumentsAndMessagesPanel from "../documents/DocumentsAndMessagesPanel";
import { useViewerSessionState } from "../documents/sessionState";
import ExitAccountModal from "../exit/ExitAccountModal";
import ForceExitWarningModal from "../exit/ForceExitWarningModal";
import FraudWarningModal from "../exit/FraudWarningModal";
import IncomeChangeWarningModal from "../exit/IncomeChangeWarningModal";
import { fraudVerdictQuery, useFraudVerdict } from "../fraud/api/useFraudVerdict";
import { releaseHeldVlaLock } from "../lock/api/releaseHeldVlaLock";
import { useVlaLock } from "../lock/api/useVlaLock";
import DealLockBanner from "../lock/components/DealLockBanner";
import { ReadOnlyProvider } from "../lock/ReadOnlyContext";
import StipulationsPanel from "../stipulations/StipulationsPanel";
import { applicationStatusQuery, useApplicationStatus } from "./api/useApplicationStatus";
import { useDscWorkbench } from "./api/useDscWorkbench";
import DealIntelligenceCard from "./components/DealIntelligenceCard";
import { mapDashboardToDealIntelligenceCard } from "./components/dealIntelligenceCard.mapping";
import FooterAction from "./components/FooterAction";
import DealStatus from "./sections/DealStatus/DealStatus";
import Financial from "./sections/Financial/Financial";
import Signers from "./sections/Signers/Signers";
import Vehicle from "./sections/Vehicle/Vehicle";
import { useSaveAllSections } from "./useSaveAllSections";

/**
 * Releases the VLA In-Use lock (only if the current user holds it) before
 * leaving the deal, so the next analyst isn't blocked by a stale lock. Awaits
 * the release so the DELETE lands before we navigate away (best-effort — a
 * failed release never blocks the exit), then redirects back to the site
 * that sent the user into the workbench.
 * @returns {Promise<void>} Resolves once the release attempt settles.
 */
async function handleExit(): Promise<void> {
  await releaseHeldVlaLock();
  redirectToOriginSite();
}

/**
 * Releases the held VLA In-Use lock before signing out, for the same reason as
 * {@link handleExit}.
 * @returns {Promise<void>} Resolves once the release attempt settles.
 */
async function handleSignOut(): Promise<void> {
  await releaseHeldVlaLock();
  // TODO: perform the actual sign-out call once wired to AuthProvider — await
  // it before redirecting so navigating away doesn't abort the request.
  redirectToOriginSite();
}

function ReviewPage() {
  const { t } = useTranslation();
  const { approvalId } = useParams<{ approvalId: string }>();
  const { user } = useAuth();

  // Acquire the VLA In-Use lock for this deal on entry; release on exit. When
  // another analyst holds it (or acquisition fails), the deal is read-only and
  // DealLockBanner explains why.
  useVlaLock(approvalId ?? "");
  // Read-only whenever we don't hold the lock (acquiring, held-by-other, or
  // error) — hide Save entirely in every one of those states. This alone
  // still gates Stipulations/Actions/Docs & Msgs, which don't have
  // role/deal-state gating built yet.
  const readOnly = useLockReadOnly();

  // Role + deal status/phase + VLA-lock verdicts per Step6 section (matrix
  // §4.1-4.4/§5.1-§5.5) — combines with `readOnly` above for the Driver/2nd
  // Signer sections specifically, since those already have per-section
  // deal-state rules ported (see useWorkbenchPermissions).
  const permissions = useWorkbenchPermissions(approvalId ?? "");
  const checkFinancialAccess = useFinancialAccessGuard(approvalId ?? "");
  // Independent per section — DRIVER_R/SECONDSIGNER_R are separate role
  // markers (matrix §2a), so one signer's read-only verdict must not lock the
  // other's fields (see Signers.tsx's per-column fieldset split).
  const driverReadOnly = readOnly || !permissions.sections.driver.editable;
  const secondSignerReadOnly = readOnly || !permissions.sections.secondSigner.editable;
  const financialReadOnly = readOnly || !permissions.sections.financial.editable;

  // The footer Exit button opens the "Exit the Account" modal; the actual
  // release-lock + navigate side-effects run once the analyst confirms there.
  const [exitModalOpen, setExitModalOpen] = useState(false);

  // Two independent reasons skip the modal entirely: the deal's VLA lock
  // isn't held by this analyst (readOnly), or the role's capabilities don't
  // include EXIT. The capability half is gated on !permissions.isLoading —
  // canExit fails safe to false before the role-permissions fetch resolves,
  // and this bypass is destructive (releases the lock + navigates away), so
  // firing it on that transient false would silently exit an EXIT-capable
  // analyst who clicks fast. readOnly has no such race (separate query) and
  // bypasses immediately. The gate is shared; the bypass action below still
  // branches per reason — readOnly never held the lock so it must never call
  // releaseHeldVlaLock, rather than leaning on that function's own no-op
  // guard to make it safe.
  const shouldBypassExitModal = readOnly || (!permissions.isLoading && !permissions.canExit);

  // Force-exit warning modal — shown when pre-approval is voided and
  // user attempts to save. Blocks the save and prompts the analyst to exit.
  const [forceExitModalOpen, setForceExitModalOpen] = useState(false);

  // Drive the one-window-per-document viewer (OR-22558): broadcast the session
  // signal to every open document window. Mounted here (not in the panel) so
  // it answers each window's handshake for the whole deal session. `!readOnly`
  // is `lock.phase === "held-by-me"` — gates each viewer window's move/check-in
  // toolbar independently of document content visibility. Stamped with this
  // deal's `approvalId` so a document window from a *different* deal (an
  // analyst can have several open as separate Review tabs) ignores it.
  useDocumentBroadcaster(useViewerSessionState(), !readOnly, approvalId ?? "");

  // React to any successful docs/{docId}/actions call from a document window
  // (check-in, move-to-approval, move-to-state): refresh the docs list.
  useDocumentActionListener(approvalId ?? "");

  // Fetch the DSC Workbench dashboard for this deal. Both the Deal Intelligence
  // Card and the Signers card derive their data from this response.
  const { data, isLoading, isError, error } = useDscWorkbench(approvalId ?? "");

  // Application status + phase is a separate, non-blocking read. The card
  // renders as soon as the dashboard resolves; status/phase populate once this
  // call returns, and a failure leaves the status blank (no card-level error).
  const { data: applicationStatus } = useApplicationStatus(approvalId ?? "");

  // Contract Vehicle's Odometer Disc Mileage / CPO Date / Total Selling Price are the only Vehicle fields
  // with an editable rule ported so far (matrix §4.1/§5.2/§5.3) — the rest of the card stays read-only via
  // Vehicle's own `editable` default. Pre-approval deals keep them locked on top of the lock/role verdicts.
  const contractVehicleEditable =
    !readOnly && permissions.sections.vehicle.editable && !isPreApprovalApp(applicationStatus);

  // Footer Save hides for the same VLA-lock reason as everything else, plus
  // once the deal reaches Contract Completed — legacy Step6Bean.buildScreen's
  // showSaveButton rule, ported 1:1 (see FOOTER_SAVE_VISIBILITY_SPEC.md).
  const hideSave = readOnly || isContractCompleted(applicationStatus);

  // Informed fraud verdict (SVRS-57313) — read once roles, the VLA lock, and
  // editability have all resolved and the Save button is actually visible
  // (role permissions fail safe to canExit=false/loading before they resolve,
  // same race called out above for shouldBypassExitModal — don't fire this
  // read before we know the analyst can edit, or an eventual re-check would
  // have nothing meaningful to compare against). Seeded once from the first
  // observed value (same idea as incomeChangeStore's baseline capture) as the
  // baseline to compare against the fresh re-check run at exit time — fixed
  // to "what it was at entry" even though the query itself always re-fetches
  // fresh (staleTime: 0).
  const canCheckFraudVerdict = !permissions.isLoading && !hideSave;
  const { data: fraudVerdict } = useFraudVerdict(approvalId ?? "", canCheckFraudVerdict);
  const entryFraudDetectedReference = useRef<boolean | null>(null);
  useEffect(() => {
    if (entryFraudDetectedReference.current === null && fraudVerdict) {
      entryFraudDetectedReference.current = fraudVerdict.fraudDetected ?? false;
    }
  }, [fraudVerdict]);

  // Check if the force-exit condition is met (pre-approval voided when not in read-only
  // mode). This triggers immediately on app load and blocks saves if the condition is true.
  const { shouldForceExit } = useForceExitCheck({
    isPreApprovalVoid: applicationStatus?.isPreApprovalVoid,
    readOnly,
  });

  // Update the force-exit store so all components can check before mutations.
  const setShouldForceExit = useForceExitStore((state) => state.setShouldForceExit);
  useEffect(() => {
    setShouldForceExit(shouldForceExit);
  }, [shouldForceExit, setShouldForceExit]);

  // If force-exit condition is met on load, show the warning immediately.
  useEffect(() => {
    if (shouldForceExit) {
      setForceExitModalOpen(true);
    }
  }, [shouldForceExit]);

  const queryClient = useQueryClient();
  // True while handleSave's click-time status re-check is in flight — disables
  // Save so a second click can't race the fetch.
  const [isCheckingForceExit, setIsCheckingForceExit] = useState(false);
  // Set when the click-time status re-check itself fails (network error) —
  // we block the save rather than risk saving over a voided pre-approval.
  const [forceExitCheckError, setForceExitCheckError] = useState<string>();

  // ReviewPage isn't remounted on approvalId route-param changes, so a
  // handleSave call started for one deal must not act on the result once the
  // analyst has navigated to another deal while the fetch was in flight.
  const approvalIdReference = useRef(approvalId);
  useEffect(() => {
    approvalIdReference.current = approvalId;
  }, [approvalId]);

  // Re-checks whether the given deal should force-exit, fresh off the BFF
  // (bypassing the query's staleTime). Shared by handleSave and
  // handleExitClick — both re-verify this at the moment of a destructive /
  // exit-adjacent action rather than trusting a stale render. Returns the raw
  // status too since handleSave also needs it for its own Contract Completed
  // check. Callers are responsible for their own approvalIdReference
  // staleness guard and for syncing useForceExitStore, since each needs to
  // interleave those differently around this read.
  const fetchForceExitStatus = useCallback(
    async (requestedApprovalId: string) => {
      const status = await queryClient.fetchQuery({
        ...applicationStatusQuery(requestedApprovalId),
        staleTime: 0,
      });
      const isForceExit = computeShouldForceExit({ isPreApprovalVoid: status.isPreApprovalVoid, readOnly });
      return { status, isForceExit };
    },
    [queryClient, readOnly]
  );

  // Orchestrates every registered editable section (Signers today; more to
  // come) through one aggregate save call — see useSaveAllSections.
  const { saveAll, isSaving } = useSaveAllSections();

  // Set whenever a Save attempt bails out for a reason that isn't a save
  // failure (a duplicate click, the force-exit modal opening, or the analyst
  // declining the income warning) so the footer's toast can stay silent
  // instead of reporting a false failure.
  const wasSaveCancelledReference = useRef(false);

  // Set only when saveAll actually reached the save API — client-side
  // short-circuits (no sections registered, validation failure) never touch
  // the network, so the footer toast must not report them as an API failure.
  const wasApiCallMadeReference = useRef(false);

  // Set to the save response's HTTP status whenever the API was reached —
  // any 2xx counts as a real save (result.ok), but the footer's success
  // toast should only fire for an exact 200, not e.g. 201/204.
  // eslint-disable-next-line unicorn/no-useless-undefined -- required: this project's React types need an explicit initializer even for `T | undefined`
  const lastSaveStatusCodeReference = useRef<number | undefined>(undefined);

  // FooterAction's Save button lives outside any section's form (as a
  // ReviewPage sibling), so the orchestrator's pending state has to travel
  // through a store rather than props.
  const setIsSaving = useSaveStatusStore((state) => state.setIsSaving);
  useEffect(() => {
    setIsSaving(isSaving);
  }, [isSaving, setIsSaving]);

  // Handle save: re-fetch application status fresh (bypassing the query's
  // staleTime) at the moment Save is clicked, so the force-exit decision uses
  // the latest isPreApprovalVoid rather than whatever was cached on load. If
  // voided, show the warning modal and abort; otherwise validate and save
  // every registered section. Shared by the footer Save button and the Exit
  // modal's Save & Exit — resolves `true` only once every section actually
  // saves, so callers can gate exit-navigation on it.
  // TODO: wire to AuthProvider once it exists (see folder-structure.md §providers)
  const handleSave = useCallback(async (): Promise<boolean> => {
    wasApiCallMadeReference.current = false;

    if (isCheckingForceExit) {
      return false;
    }

    if (!approvalId) {
      setForceExitCheckError(t("forceExit.missingApprovalId"));
      return false;
    }

    const requestedApprovalId = approvalId;
    setForceExitCheckError(undefined);
    setIsCheckingForceExit(true);
    try {
      const { status, isForceExit } = await fetchForceExitStatus(requestedApprovalId);

      // Deal changed while the fetch was in flight — this result no longer
      // applies; don't pop the modal or submit against the wrong deal.
      if (approvalIdReference.current !== requestedApprovalId) {
        return false;
      }

      // Sync the shared store immediately so useSaveWorkbench's mutationFn
      // (which reads the store's live value, not a stale render) agrees with
      // this fresh check before saveAll runs synchronously.
      setShouldForceExit(isForceExit);

      if (isForceExit) {
        setForceExitModalOpen(true);
        wasSaveCancelledReference.current = true;
        return false;
      }

      // hideSave only hides the footer button (stale-ish applicationStatus,
      // see above) — the Exit modal's Save & Exit calls this same function
      // directly, so it needs its own fresh-status block or a deal that
      // reached Contract Completed after the page loaded could still be
      // saved through that path. ExitAccountModal already renders a generic
      // "couldn't save" message on any `false` return (see its saveError).
      if (isContractCompleted(status)) {
        wasSaveCancelledReference.current = true;
        return false;
      }

      const result = await saveAll(requestedApprovalId);
      wasApiCallMadeReference.current = result.apiCallMade;
      lastSaveStatusCodeReference.current = result.statusCode;
      return result.ok;
    } catch (statusCheckError) {
      // A cancelled/superseded query (e.g. dedup'd against another refetch)
      // isn't a real status-check failure — don't block Save or show an error.
      if (approvalIdReference.current === requestedApprovalId && !(statusCheckError instanceof CancelledError)) {
        setForceExitCheckError(getErrorMessage(statusCheckError));
      }
      return false;
    } finally {
      setIsCheckingForceExit(false);
    }
  }, [approvalId, isCheckingForceExit, fetchForceExitStatus, saveAll, setShouldForceExit, t]);

  // CAPS WARNING — shown before either Save entry point (footer Save or the
  // Exit modal's Save & Exit) commits a driver/second-signer combined gross
  // income change, since that also updates Step 2's dealer income + advance
  // calc. Resolves the pending confirmAndSave call once the analyst answers.
  const [incomeWarningOpen, setIncomeWarningOpen] = useState(false);
  const incomeWarningResolveReference = useRef<((proceed: boolean) => void) | null>(null);
  // Guards the whole confirm-then-save flow so a second Save/Save & Exit
  // click can't fire while a warning is already pending an answer (or while
  // the save it gated is still in flight) — a second run would orphan the
  // first pending Promise's resolver.
  const confirmAndSaveInFlightReference = useRef(false);

  const handleIncomeWarningOpenChange = useCallback((nextOpen: boolean) => {
    setIncomeWarningOpen(nextOpen);
    if (!nextOpen && incomeWarningResolveReference.current) {
      incomeWarningResolveReference.current(false);
      incomeWarningResolveReference.current = null;
    }
  }, []);

  const handleIncomeWarningConfirm = useCallback(() => {
    incomeWarningResolveReference.current?.(true);
    incomeWarningResolveReference.current = null;
    setIncomeWarningOpen(false);
  }, []);

  const confirmAndSave = useCallback(async (): Promise<boolean> => {
    if (confirmAndSaveInFlightReference.current) {
      wasSaveCancelledReference.current = true;
      return false;
    }
    confirmAndSaveInFlightReference.current = true;
    wasSaveCancelledReference.current = false;
    try {
      // Address Standardization (address-standardization-spec.md §3/§7):
      // verifies any changed address slot before the actual save, sequencing
      // through AddressVerificationModal as needed. Runs before the income
      // warning — neither depends on the other.
      const addressVerificationProceeded = await runAddressVerificationGate();
      if (!addressVerificationProceeded) {
        wasSaveCancelledReference.current = true;
        return false;
      }

      if (getHasCombinedIncomeChanged()) {
        const proceed = await new Promise<boolean>((resolve) => {
          incomeWarningResolveReference.current = resolve;
          setIncomeWarningOpen(true);
        });
        if (!proceed) {
          wasSaveCancelledReference.current = true;
          return false;
        }
      }
      return await handleSave();
    } finally {
      confirmAndSaveInFlightReference.current = false;
    }
  }, [handleSave]);

  // CAPS Warning - Potential Fraud (SVRS-57313) — shown only when the
  // Informed verdict flipped false -> true during this review session (an
  // already-true entry verdict means Analyst Notes already surfaced the
  // Informed link, so the pop-up would be redundant). Only gates the normal
  // Exit the Account flow: read-only and no-EXIT-capability analysts already
  // bypass that modal entirely, so they bypass this check too. Continue opens
  // the normal Exit the Account screen; Return to Account (or dismissing the
  // dialog) is a no-op — nothing has been saved or moved yet at this point.
  const [fraudWarningOpen, setFraudWarningOpen] = useState(false);

  const handleExitClick = useCallback(async (): Promise<void> => {
    if (!approvalId) {
      setExitModalOpen(true);
      return;
    }
    // ReviewPage isn't remounted on approvalId route-param changes (same
    // race handleSave guards against) — a click started for one deal must
    // not act on the result once the analyst has navigated to another deal
    // while these fetches were in flight.
    const requestedApprovalId = approvalId;

    // Force-exit takes priority over the fraud check — re-verify the deal's
    // status fresh (same click-time re-check handleSave does) before even
    // considering the fraud verdict, since a voided pre-approval forces the
    // analyst out regardless of what Informed found.
    try {
      const { isForceExit } = await fetchForceExitStatus(requestedApprovalId);
      if (approvalIdReference.current !== requestedApprovalId) {
        return;
      }
      setShouldForceExit(isForceExit);
      if (isForceExit) {
        setForceExitModalOpen(true);
        return;
      }
    } catch {
      // A status-check failure must never trap the analyst in the account —
      // fall through to the normal fraud check below.
    }

    // Skip the fraud re-check entirely unless we have a confirmed "not
    // flagged" baseline — `null` means the baseline read hasn't resolved yet
    // (so we genuinely can't tell whether this is a flip), and `true` means
    // it was already flagged at entry (Analyst Notes already covers it).
    // Either way there's nothing useful this click can determine, so proceed
    // straight to the normal Exit the Account screen rather than guessing.
    if (entryFraudDetectedReference.current !== false) {
      setExitModalOpen(true);
      return;
    }

    let fraudDetected = false;
    try {
      const verdict = await queryClient.fetchQuery({ ...fraudVerdictQuery(requestedApprovalId), staleTime: 0 });
      fraudDetected = verdict.fraudDetected ?? false;
    } catch {
      // A BFF/chassis hiccup must never trap the analyst in the account.
      fraudDetected = false;
    }

    if (approvalIdReference.current !== requestedApprovalId) {
      return;
    }
    if (fraudDetected) {
      setFraudWarningOpen(true);
    } else {
      setExitModalOpen(true);
    }
  }, [approvalId, queryClient, fetchForceExitStatus, setShouldForceExit]);

  const handleFraudWarningContinue = useCallback(() => {
    setFraudWarningOpen(false);
    setExitModalOpen(true);
  }, []);

  // Guards the footer Exit button against a double-click: handleExitClick's
  // force-exit/fraud-verdict re-checks (and handleExit's lock release) are
  // async, so a fast second click could otherwise fire two overlapping
  // attempts whose results land out of order and flash two different
  // modals — or double-release the lock — for one user action.
  const [isExiting, setIsExiting] = useState(false);

  const handleFooterExit = useCallback(async (): Promise<void> => {
    if (isExiting) {
      return;
    }
    setIsExiting(true);
    try {
      if (!shouldBypassExitModal) {
        await handleExitClick();
        return;
      }
      if (readOnly) {
        // Never held the lock — nothing to release, same as Step 6.
        redirectToOriginSite();
        return;
      }
      // Held the lock but the role lacks EXIT: release it before leaving so
      // the next analyst isn't blocked by it.
      await handleExit();
    } finally {
      setIsExiting(false);
    }
  }, [isExiting, shouldBypassExitModal, handleExitClick, readOnly]);

  // Footer Save's own toast, distinct from Save & Exit's inline modal error —
  // the modal already summarizes failures itself (see ExitAccountModal). Only
  // reflects an actual save-API response: cancellations and client-side
  // validation short-circuits (no network call made) stay silent. The
  // success toast is further restricted to an exact 200 — a 2xx like 201/204
  // still counts as a successful save (result.ok), but stays silent here
  // rather than claiming "Changes saved." for a non-200 response.
  const handleFooterSave = useCallback(async () => {
    const saved = await confirmAndSave();
    if (saved) {
      if (lastSaveStatusCodeReference.current === 200) {
        toast.success(t("review.footerSave.success"));
      }
    } else if (!wasSaveCancelledReference.current && wasApiCallMadeReference.current) {
      toast.error(t("review.footerSave.error"));
    }
  }, [confirmAndSave, t]);

  const dealCardViewModel = data
    ? mapDashboardToDealIntelligenceCard(data, approvalId ?? "", applicationStatus)
    : undefined;

  return (
    <ReadOnlyProvider value={readOnly}>
      <div className="bg-background text-foreground flex h-screen flex-col overflow-hidden">
        <TopBar userName={user?.name} userRole={user?.userRole} onSignOut={() => void handleSignOut()} />
        <TwoColumnLayout
          firstColumn={
            <>
              {/* VLA In-Use lock banner — shown only when another analyst holds the
                  lock or acquisition failed; otherwise renders nothing. */}
              <DealLockBanner />
              {/* Deal Intelligence Card — loading skeleton, error, or live data. */}
              {isLoading && (
                <div
                  role="status"
                  aria-label={t("dealIntelligenceCard.loading")}
                  className="bg-primary sticky top-0 z-10 rounded px-3 py-2 lg:px-4 lg:py-3"
                >
                  <Skeleton loading active paragraph={{ rows: 2, widths: [220, 320] }} />
                </div>
              )}
              {dealCardViewModel && <DealIntelligenceCard data={dealCardViewModel} />}

              <div className="flex w-full flex-col gap-2 py-2 sm:min-h-0 sm:flex-1 sm:overflow-y-auto">
                {isError && (
                  <ErrorState message={t("dealIntelligenceCard.loadError", { message: getErrorMessage(error) })} />
                )}
                {/* key on approvalId so switching deals remounts the form and re-seeds
                    its defaultValues from the new response (RHF reads them once at mount). */}
                {data && (
                  <>
                    <Signers
                      key={approvalId}
                      driver={data.driver}
                      secondSigner={data.secondSigner}
                      // Role-only visibility (matrix §2a: Driver/2nd Signer have
                      // no deal-state hiding rule) — a boolean render-time gate,
                      // NOT a swap of the `driver`/`secondSigner` data itself:
                      // useSignersForm hydrates RHF's defaultValues once at
                      // mount, so toggling `driver` to/from undefined after
                      // permissions resolve asynchronously would freeze the form
                      // on stale (empty) defaults instead of the real data.
                      driverVisible={permissions.sections.driver.visible}
                      secondSignerVisible={permissions.sections.secondSigner.visible}
                      approvalId={approvalId}
                      tier={dealCardViewModel?.tier}
                      isPreApprovalApp={isPreApprovalApp(applicationStatus)}
                      driverReadOnly={driverReadOnly}
                      secondSignerReadOnly={secondSignerReadOnly}
                      autopay={data.autopay}
                    />
                    <Vehicle
                      key={approvalId}
                      vehicle={data.vehicle}
                      condition={data.financial?.vehicle?.condition}
                      contractVehicleEditable={contractVehicleEditable}
                      amountFinanced={data.financial?.tila?.amountFinanced}
                      vsc={data.additionalProducts?.vsc?.[0]}
                      gap={data.additionalProducts?.gap?.[0]}
                    />
                    <DealStatus
                      approvalId={approvalId ?? ""}
                      isPreApprovalApp={isPreApprovalApp(applicationStatus)}
                      dealStatus={data.dealStatus}
                      // @TODO: Determine if there are any additional conditions for TPAP product visibility
                      hasTpapProduct={(data.additionalProducts?.tpap?.length ?? 0) > 0}
                    />
                    {permissions.sections.financial.visible && (
                      <Financial
                        key={approvalId}
                        approvalId={approvalId ?? ""}
                        hasCorrectionLetter={data.financial?.correctionLetter?.hasCorrectionLetter ?? false}
                        contractDate={data.financial?.contractDate}
                        registrationState={data.financial?.registrationState}
                        receiptDate={data.financial?.receiptDate}
                        tila={data.financial?.tila}
                        vehicle={data.financial?.vehicle}
                        financial={data.financial}
                        warnings={data.metadata?.warnings}
                        readOnly={financialReadOnly}
                        onCheckAccess={checkFinancialAccess}
                      />
                    )}
                  </>
                )}
              </div>
              {forceExitCheckError && (
                <p role="alert" className="text-destructive px-3 py-2 text-xs">
                  {t("forceExit.checkError", { message: forceExitCheckError })}
                </p>
              )}
              <FooterAction
                onSave={() => void handleFooterSave()}
                onExit={() => void handleFooterExit()}
                hideSave={hideSave}
                isCheckingForceExit={isCheckingForceExit}
                isExiting={isExiting}
              />
            </>
          }
          secondColumn={
            <>
              <DocumentsAndMessagesPanel approvalId={approvalId ?? ""} dealerId={data?.dealSummary?.dealerId} />
              <StipulationsPanel approvalId={approvalId ?? ""} />
            </>
          }
        />
        <PageFooter />
        <ActionFabCluster approvalId={approvalId ?? ""} driver={data?.driver} secondSigner={data?.secondSigner} />
        <ExitAccountModal
          open={exitModalOpen}
          onOpenChange={setExitModalOpen}
          approvalId={approvalId ?? ""}
          currentStatus={applicationStatus?.statusDescription}
          currentPhase={applicationStatus?.phaseDescription}
          roleName={user?.userRole}
          onSaveApplicant={confirmAndSave}
          onExit={() => void handleExit()}
        />
        <ForceExitWarningModal open={forceExitModalOpen} onOpenChange={setForceExitModalOpen} onConfirm={handleExit} />
        <IncomeChangeWarningModal
          open={incomeWarningOpen}
          onOpenChange={handleIncomeWarningOpenChange}
          onConfirm={handleIncomeWarningConfirm}
        />
        <FraudWarningModal
          open={fraudWarningOpen}
          onOpenChange={setFraudWarningOpen}
          onContinue={handleFraudWarningContinue}
        />
      </div>
    </ReadOnlyProvider>
  );
}

export default ReviewPage;
